import { FaqItem } from "../models/home/faq-item";

/**
 * Content model for the Death Verification (VerifyDeceased) marketing cluster.
 *
 * This vertical markets our deceased-identity verification product from the
 * LeadCruncher domain so the pillar page inherits the domain authority we have
 * already built with property, location and sold-listing pages.
 */

export interface DvStat {
  value: string;
  label: string;
  caption: string;
}

export interface DvFeature {
  icon: string;
  title: string;
  description: string;
}

export interface DvStep {
  step: string;
  title: string;
  description: string;
}

export interface DvComparisonRow {
  criteria: string;
  verifyDeceased: string;
  deathMasterFile: string;
  advantage: boolean;
}

export interface DvIndustry {
  slug: string;
  name: string;
  navLabel: string;
  icon: string;
  metaTitle: string;
  metaDescription: string;
  heroBadge: string;
  heroTitle: string;
  heroHighlight: string;
  heroDescription: string;
  painPoints: string[];
  outcomes: DvFeature[];
  useCase: {
    title: string;
    body: string;
  };
  metrics: DvStat[];
  faqs: FaqItem[];
}

export interface DvApiEndpoint {
  method: string;
  path: string;
  title: string;
  description: string;
}

/** Public-facing product name. Kept in one place for an easy rebrand. */
export const DvBrand = {
  productName: "Verify Deceased",
  legalName: "Verify Deceased by LeadCruncher",
  tagline: "Know within days, not months.",
  supportEmail: "morry@leadcruncher.com",
  supportPhone: "+1-909-315-5330"
} as const;

export const DvStats: DvStat[] = [
  {
    value: "7 Days",
    label: "Average time to record",
    caption: "Deceased records surface in our index within a week of the date of death."
  },
  {
    value: "50 States",
    label: "Nationwide coverage",
    caption: "Sourced continuously from every US state and thousands of local jurisdictions."
  },
  {
    value: "Weekly",
    label: "Index refresh",
    caption: "New decedent records are ingested, matched and published every Week."
  },
  {
    value: "<300ms",
    label: "Median API response",
    caption: "Real-time verification fast enough to sit inline in your onboarding flow."
  }
];

export const DvHeroHighlights: string[] = [
  "Records within days of death, not the 30 to 90 day lag of federal files",
  "Real-time REST API plus bulk batch screening",
  "Built for banks, lenders, insurers, pensions and government agencies"
];

export const DvFeatures: DvFeature[] = [
  {
    icon: "fa-solid fa-bolt",
    title: "The fastest deceased index in the US",
    description:
      "We ingest decedent records directly at the source, which means a death typically appears in our index within a week. Most national files take one to three months to reflect the same event, and that gap is exactly where fraud happens."
  },
  {
    icon: "fa-solid fa-shield-halved",
    title: "Stop ghosting and synthetic identity fraud",
    description:
      "Fraud rings harvest the identities of recently deceased Americans to open credit cards, claim benefits, secure employment and drain accounts. Screening against a current deceased index closes that window before a loss is booked."
  },
  {
    icon: "fa-solid fa-plug-circle-bolt",
    title: "Drop-in REST API",
    description:
      "A single authenticated POST returns a deterministic match verdict, confidence score and date of death. JSON in, JSON out. Most teams are live in a day, with sandbox keys available immediately."
  },
  {
    icon: "fa-solid fa-layer-group",
    title: "Bulk portfolio screening",
    description:
      "Upload an entire customer, member or policyholder file and get a scored deceased-match report back. Ideal for quarterly portfolio hygiene, escheatment reviews and pre-audit clean-up."
  },
  {
    icon: "fa-solid fa-bullseye",
    title: "Deterministic matching, not guesswork",
    description:
      "Matching runs on name, date of birth, SSN and last known address with a transparent confidence score on every hit, so your analysts know exactly why a record matched before they action it."
  },
  {
    icon: "fa-solid fa-file-shield",
    title: "Audit-ready evidence trail",
    description:
      "Every verification is logged with a timestamp, the matching criteria used and the source classification, giving you the documentation your compliance, audit and regulatory reviewers ask for."
  }
];

export const DvSteps: DvStep[] = [
  {
    step: "01",
    title: "Send the identity",
    description:
      "Post the name, date of birth and any combination of SSN or last known address to our verification endpoint, or upload a batch file through the dashboard."
  },
  {
    step: "02",
    title: "We match against the live index",
    description:
      "The identity is scored against our continuously refreshed nationwide deceased index, built from source records rather than downstream aggregators."
  },
  {
    step: "03",
    title: "Get a decision-ready verdict",
    description:
      "You receive a clear deceased / not-found verdict, a confidence score, the date of death where available and a reference ID you can store for audit."
  },
  {
    step: "04",
    title: "Act before the loss",
    description:
      "Block the application, freeze the account, pause the benefit payment or route to manual review, all before funds move and while recovery is still possible."
  }
];

export const DvTrustSignals: DvFeature[] = [
  {
    icon: "fa-solid fa-lock",
    title: "Encrypted end to end",
    description: "TLS 1.2+ in transit and encryption at rest. We never resell the identities you submit."
  },
  {
    icon: "fa-solid fa-scale-balanced",
    title: "Permissible-use governed",
    description: "Access is contracted to vetted institutions with a documented permissible business purpose."
  },
  {
    icon: "fa-solid fa-clock-rotate-left",
    title: "Retention you control",
    description: "Configurable submission retention windows so the product fits your data-governance policy."
  },
  {
    icon: "fa-solid fa-headset",
    title: "Named integration support",
    description: "Enterprise accounts get a named engineer through sandbox, certification and go-live."
  }
];

export const DvComparisonRows: DvComparisonRow[] = [
  {
    criteria: "Time from death to availability",
    verifyDeceased: "Typically within 7 days",
    deathMasterFile: "Commonly 30 to 90 days, sometimes longer",
    advantage: true
  },
  {
    criteria: "Update frequency",
    verifyDeceased: "Daily ingestion and publication",
    deathMasterFile: "Weekly or monthly file drops",
    advantage: true
  },
  {
    criteria: "State-reported deaths",
    verifyDeceased: "Captured directly from source jurisdictions",
    deathMasterFile: "Public DMF excludes state-protected records",
    advantage: true
  },
  {
    criteria: "Access model",
    verifyDeceased: "Real-time REST API, batch upload and dashboard",
    deathMasterFile: "Flat-file subscription requiring in-house processing",
    advantage: true
  },
  {
    criteria: "Match transparency",
    verifyDeceased: "Confidence score and matched-field breakdown per hit",
    deathMasterFile: "Raw records; you build the matching logic yourself",
    advantage: true
  },
  {
    criteria: "Certification overhead",
    verifyDeceased: "Standard commercial agreement and onboarding review",
    deathMasterFile: "Limited Access DMF certification and periodic audits",
    advantage: true
  },
  {
    criteria: "Engineering lift to deploy",
    verifyDeceased: "One endpoint; most teams integrate in under a day",
    deathMasterFile: "Ingest pipeline, storage and matching engine required",
    advantage: true
  }
];

export const DvFraudSignals: DvFeature[] = [
  {
    icon: "fa-solid fa-credit-card",
    title: "Credit applications in a decedent's name",
    description:
      "Cards and personal loans opened weeks after death, before any national file reflects the event. The account performs briefly, then busts out."
  },
  {
    icon: "fa-solid fa-id-card",
    title: "Employment and I-9 identity theft",
    description:
      "A deceased person's SSN is used to pass onboarding and payroll checks, exposing the employer to penalties and remediation costs."
  },
  {
    icon: "fa-solid fa-hand-holding-dollar",
    title: "Continued benefit and pension payments",
    description:
      "Retirement, annuity and public benefit payments keep flowing to an account controlled by someone else, creating a growing clawback liability."
  },
  {
    icon: "fa-solid fa-file-invoice-dollar",
    title: "Fraudulent insurance and tax claims",
    description:
      "Claims and refunds filed against a decedent's identity, often detected only after the payout has already cleared."
  },
  {
    icon: "fa-solid fa-user-secret",
    title: "Synthetic identity seeding",
    description:
      "A real deceased SSN is blended with a fabricated name and date of birth to build a synthetic profile that looks clean to legacy checks."
  },
  {
    icon: "fa-solid fa-building-columns",
    title: "Account takeover during estate limbo",
    description:
      "In the weeks between death and probate, dormant accounts are drained while no one is actively monitoring the relationship."
  }
];

export const DvApiEndpoints: DvApiEndpoint[] = [
  {
    method: "POST",
    path: "/v1/verify",
    title: "Single identity verification",
    description: "Real-time deceased check for one identity. Returns a verdict, confidence score and date of death."
  },
  {
    method: "POST",
    path: "/v1/verify/batch",
    title: "Batch verification",
    description: "Submit up to 10,000 identities per request for asynchronous portfolio screening."
  },
  {
    method: "GET",
    path: "/v1/verify/{referenceId}",
    title: "Retrieve a prior result",
    description: "Fetch a stored verification by reference ID for audit, dispute handling or reconciliation."
  },
  {
    method: "POST",
    path: "/v1/monitoring/subscribe",
    title: "Continuous monitoring",
    description: "Register a portfolio for ongoing surveillance and receive a webhook the moment a match appears."
  }
];

export const DvApiRequestSample = `curl -X POST https://api.verifydeceased.com/v1/verify \\
  -H "Authorization: Bearer $VD_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "firstName": "Robert",
    "lastName": "Hensley",
    "dateOfBirth": "1948-03-11",
    "ssn": "***-**-4417",
    "address": {
      "line1": "8023 Jennet Street",
      "city": "Alta Loma",
      "state": "CA",
      "postalCode": "91701"
    }
  }'`;

export const DvApiResponseSample = `{
  "referenceId": "vd_9f31c0a7be2d",
  "status": "deceased",
  "confidence": 0.98,
  "dateOfDeath": "2026-06-19",
  "matchedFields": ["lastName", "dateOfBirth", "ssn", "state"],
  "recordAgeInDays": 6,
  "source": "state_vital_record",
  "verifiedAt": "2026-06-25T14:22:08Z"
}`;

export const DvApiCapabilities: DvFeature[] = [
  {
    icon: "fa-solid fa-gauge-high",
    title: "Sub-second responses",
    description: "A median response under 300ms means the check runs inline without slowing your application flow."
  },
  {
    icon: "fa-solid fa-code-branch",
    title: "Sandbox before you sign",
    description: "Deterministic sandbox fixtures let your team build and test the full integration ahead of contracting."
  },
  {
    icon: "fa-solid fa-tower-broadcast",
    title: "Webhook monitoring",
    description: "Subscribe a portfolio once and get pushed a notification the moment any enrolled identity matches."
  },
  {
    icon: "fa-solid fa-arrows-rotate",
    title: "Idempotent and retry-safe",
    description: "Every request accepts an idempotency key so network retries never double-bill or duplicate a record."
  },
  {
    icon: "fa-solid fa-chart-line",
    title: "Usage analytics",
    description: "Track volume, match rate and latency by environment and API key from the dashboard."
  },
  {
    icon: "fa-solid fa-server",
    title: "Built for scale",
    description: "Rate limits and throughput tiers sized for high-volume onboarding and nightly portfolio runs."
  }
];

export const DvIntegrationSteps: DvStep[] = [
  {
    step: "01",
    title: "Request sandbox access",
    description: "Tell us your use case and volume. We issue sandbox credentials and the full API reference."
  },
  {
    step: "02",
    title: "Build against fixtures",
    description: "Deterministic test identities cover deceased, not-found and low-confidence paths end to end."
  },
  {
    step: "03",
    title: "Certify and contract",
    description: "We review your permissible use, agree throughput tiers and countersign the data agreement."
  },
  {
    step: "04",
    title: "Go live with support",
    description: "Production keys are issued and a named engineer monitors your first weeks of live traffic."
  }
];

export const DvIndustries: DvIndustry[] = [
  {
    slug: "banks-and-credit-unions",
    name: "Banks & Credit Unions",
    navLabel: "Banks & Credit Unions",
    icon: "fa-solid fa-building-columns",
    metaTitle: "Death Verification for Banks & Credit Unions | Verify Deceased",
    metaDescription:
      "Screen account openings and existing members against a deceased index updated within days of death. Stop decedent account fraud before funds move.",
    heroBadge: "Banking & Credit Unions",
    heroTitle: "Catch decedent account fraud",
    heroHighlight: "before the funds move",
    heroDescription:
      "Deceased-identity abuse concentrates in the weeks right after death, exactly the window where federal death files are still silent. Screening every application and periodically re-screening your book closes it.",
    painPoints: [
      "New accounts opened with a recently deceased customer's identity pass legacy KYC checks cleanly",
      "Dormant accounts are drained during the gap between date of death and probate notification",
      "Federal file lag means the death is confirmed only after the loss has already been booked",
      "Escheatment and dormancy reviews depend on stale deceased data"
    ],
    outcomes: [
      {
        icon: "fa-solid fa-user-check",
        title: "Cleaner account opening",
        description: "An inline check at onboarding rejects decedent identities before an account number is ever issued."
      },
      {
        icon: "fa-solid fa-satellite-dish",
        title: "Continuous book monitoring",
        description: "Enrol your existing customer file once and get alerted the moment an accountholder matches."
      },
      {
        icon: "fa-solid fa-piggy-bank",
        title: "Accurate dormancy handling",
        description: "Distinguish genuinely dormant accounts from decedent accounts before escheatment deadlines."
      },
      {
        icon: "fa-solid fa-clipboard-check",
        title: "Evidence for examiners",
        description: "Timestamped verification records support your BSA/AML and internal audit documentation."
      }
    ],
    useCase: {
      title: "Where it fits in a bank's stack",
      body: "Most institutions call the verification endpoint at two points: inline during digital account opening, and as a nightly or weekly batch across the existing customer file. The inline call returns in well under a second so it sits comfortably inside an onboarding decision, while the batch run feeds an exception queue your fraud and operations teams work the next morning."
    },
    metrics: [
      { value: "7 Days", label: "To record a death", caption: "Versus the one-to-three month lag most institutions work with today." },
      { value: "Inline", label: "At account opening", caption: "Fast enough to run in the live onboarding decision path." },
      { value: "Nightly", label: "Portfolio re-screening", caption: "Continuous surveillance across your entire customer book." }
    ],
    faqs: [
      {
        question: "How does this differ from the death screening already in our KYC vendor's stack?",
        answer:
          "Most KYC and identity vendors resell a downstream copy of the Social Security Administration's Death Master File, which typically reflects a death one to three months after it occurs. We source decedent records directly from state and local jurisdictions, so a death normally appears in our index within about a week. If your current provider is built on the DMF, you are effectively blind during the highest-risk window."
      },
      {
        question: "Can we screen our entire existing member file, not just new applications?",
        answer:
          "Yes. Batch verification accepts up to 10,000 identities per request and is designed for full-portfolio runs. Many institutions do an initial full-book screen, then move to continuous monitoring where we push a webhook whenever an enrolled identity matches a newly published record."
      },
      {
        question: "What does the response tell our fraud analysts?",
        answer:
          "Each result includes a deceased or not-found verdict, a confidence score, the specific fields that matched, the date of death where available and the age of the underlying record. That lets your team triage confidently rather than manually re-verifying every alert."
      },
      {
        question: "Is a real-time check fast enough for digital account opening?",
        answer:
          "Median response time is under 300 milliseconds, which is well inside the budget of a typical onboarding decision engine. The API is also idempotent and retry-safe, so transient network issues never duplicate a verification or double-bill you."
      }
    ]
  },
  {
    slug: "lenders-and-credit-issuers",
    name: "Lenders & Credit Issuers",
    navLabel: "Lenders & Credit",
    icon: "fa-solid fa-credit-card",
    metaTitle: "Death Verification for Lenders & Card Issuers | Verify Deceased",
    metaDescription:
      "Block credit applications filed with deceased identities. Real-time death verification with records surfacing within days, not months.",
    heroBadge: "Lending & Credit",
    heroTitle: "Decline decedent applications",
    heroHighlight: "at the point of decision",
    heroDescription:
      "A deceased identity with a clean credit history is one of the most valuable assets in the fraud economy. It has no one to dispute the charges and no one to notice, until the charge-off lands on your book.",
    painPoints: [
      "Credit files for the recently deceased look pristine and sail through automated underwriting",
      "Bust-out losses are recognised months later, long after recovery is realistic",
      "Synthetic profiles seeded with a decedent SSN defeat conventional identity checks",
      "First-payment-default investigations consume analyst time that prevention would have saved"
    ],
    outcomes: [
      {
        icon: "fa-solid fa-ban",
        title: "Prevent the origination",
        description: "Stop the application at decision time instead of writing off the balance three months later."
      },
      {
        icon: "fa-solid fa-magnifying-glass-dollar",
        title: "Break synthetic identities",
        description: "A decedent SSN paired with a mismatched identity is one of the strongest synthetic-fraud signals available."
      },
      {
        icon: "fa-solid fa-percent",
        title: "Lower fraud loss rate",
        description: "Removing decedent originations reduces both gross fraud losses and downstream collections spend."
      },
      {
        icon: "fa-solid fa-gauge-simple-high",
        title: "No impact on approval speed",
        description: "Sub-second responses keep instant-decision funnels instant."
      }
    ],
    useCase: {
      title: "Where it fits in an underwriting flow",
      body: "The verification call typically sits alongside your bureau pull and identity-proofing step. A deceased verdict with high confidence becomes a hard decline rule; a lower-confidence match routes to manual review with the matched-field breakdown attached so an analyst can adjudicate in seconds rather than opening a research case."
    },
    metrics: [
      { value: "<300ms", label: "Median latency", caption: "Fits inside instant-decision underwriting without adding friction." },
      { value: "0.98", label: "Typical match confidence", caption: "Deterministic scoring your rules engine can threshold on directly." },
      { value: "Daily", label: "Index refresh", caption: "New decedent records published every day, not on a monthly cycle." }
    ],
    faqs: [
      {
        question: "Where in our decision flow should the check run?",
        answer:
          "Most lenders place it immediately after identity proofing and alongside the bureau pull. A high-confidence deceased verdict becomes a hard decline; anything below your chosen threshold routes to manual review with the matched fields included so an analyst can adjudicate quickly."
      },
      {
        question: "Does this help against synthetic identity fraud specifically?",
        answer:
          "Yes, and it is one of the strongest available signals. Synthetic profiles are frequently built around a real SSN belonging to someone deceased, combined with a fabricated name and date of birth. When the SSN matches a decedent record but the accompanying identity attributes do not, that contradiction is a high-quality fraud indicator."
      },
      {
        question: "What volume can the API sustain?",
        answer:
          "Throughput tiers are sized to your expected application volume and agreed during onboarding. High-volume issuers run real-time checks on every application plus a nightly batch across the existing portfolio without hitting limits."
      },
      {
        question: "Can we test the integration before committing?",
        answer:
          "Yes. We issue sandbox credentials with deterministic test identities covering deceased, not-found and low-confidence paths, so your team can build and certify the full integration before any contract is signed."
      }
    ]
  },
  {
    slug: "insurance-carriers",
    name: "Insurance Carriers",
    navLabel: "Insurance",
    icon: "fa-solid fa-umbrella",
    metaTitle: "Death Verification for Insurance Carriers | Verify Deceased",
    metaDescription:
      "Validate claims and keep policyholder records current with a deceased index refreshed daily. Reduce claim fraud and unclaimed-property exposure.",
    heroBadge: "Insurance",
    heroTitle: "Validate every claim",
    heroHighlight: "against a current death index",
    heroDescription:
      "Carriers sit on both sides of the problem: fraudulent claims filed against decedent identities, and legitimate beneficiaries who were never located because the death was never detected.",
    painPoints: [
      "Claims filed using a deceased person's identity clear before verification completes",
      "Annuity and pension payments continue after the annuitant has died",
      "Unclaimed-property and beneficiary-location obligations rely on outdated death files",
      "Manual death confirmation slows genuine claims and frustrates real beneficiaries"
    ],
    outcomes: [
      {
        icon: "fa-solid fa-file-circle-check",
        title: "Faster legitimate claims",
        description: "Automated confirmation removes manual death-certificate chasing from the straightforward cases."
      },
      {
        icon: "fa-solid fa-hand",
        title: "Stop improper payments",
        description: "Detect annuity and benefit recipients who have died before the next payment cycle runs."
      },
      {
        icon: "fa-solid fa-people-arrows",
        title: "Meet beneficiary obligations",
        description: "Identify deceased policyholders proactively so beneficiary outreach starts on time."
      },
      {
        icon: "fa-solid fa-shield-halved",
        title: "Reduce claim fraud",
        description: "Cross-check claimant and insured identities against decedent records at intake."
      }
    ],
    useCase: {
      title: "Where it fits in a carrier's operations",
      body: "Two integration points dominate. At claim intake, a real-time call confirms or contradicts the reported death and routes exceptions to special investigations. Separately, a scheduled batch sweep across the in-force book and annuity payment file surfaces deceased policyholders early enough to pause the next disbursement and begin beneficiary outreach."
    },
    metrics: [
      { value: "7 Days", label: "To detect a death", caption: "Pause the next payment cycle instead of clawing funds back later." },
      { value: "Batch", label: "In-force book sweeps", caption: "Screen the entire policyholder and annuity file on a schedule." },
      { value: "Audit", label: "Ready evidence trail", caption: "Every verification logged with criteria, source class and timestamp." }
    ],
    faqs: [
      {
        question: "Can this replace collecting a death certificate?",
        answer:
          "It is best used as a fast first-pass confirmation rather than a wholesale replacement for certified documentation on high-value claims. For routine claims it removes days of manual chasing, and for the rest it tells your team immediately whether the reported death is corroborated so investigative effort is focused where it matters."
      },
      {
        question: "How does this help with unclaimed property and beneficiary location?",
        answer:
          "Regular batch sweeps of your in-force book identify deceased policyholders you have not yet been notified about. Detecting the death early lets beneficiary outreach begin within your regulatory window rather than after the policy has drifted toward escheatment."
      },
      {
        question: "Does it cover annuity and pension payment files?",
        answer:
          "Yes. Payment-file screening is one of the most common uses. Running the file before each disbursement cycle means an improper payment is prevented rather than recovered, which is materially cheaper and avoids a difficult conversation with the estate."
      },
      {
        question: "What identity fields do you need to match reliably?",
        answer:
          "Name and date of birth produce a usable match, and adding SSN or last known address raises confidence substantially. The response tells you exactly which fields matched, so you can set different action thresholds for different claim values."
      }
    ]
  },
  {
    slug: "government-agencies",
    name: "Government & Public Agencies",
    navLabel: "Government",
    icon: "fa-solid fa-landmark",
    metaTitle: "Death Verification for Government Agencies | Verify Deceased",
    metaDescription:
      "Prevent improper benefit payments with deceased records available within days. Nationwide coverage for federal, state and local agencies.",
    heroBadge: "Government & Public Sector",
    heroTitle: "Stop improper payments",
    heroHighlight: "before the next disbursement",
    heroDescription:
      "Every benefit payment issued to a deceased recipient becomes a recovery case, an audit finding and a headline. Detecting the death before the payment cycle runs avoids all three.",
    painPoints: [
      "Benefit and entitlement payments continue after a recipient dies",
      "Recovery of improper payments is expensive and frequently unsuccessful",
      "Inter-agency data sharing lags the actual date of death by weeks",
      "Audit findings and improper-payment reporting obligations create sustained pressure"
    ],
    outcomes: [
      {
        icon: "fa-solid fa-circle-stop",
        title: "Prevention over recovery",
        description: "Catching the death before disbursement is dramatically cheaper than pursuing a clawback."
      },
      {
        icon: "fa-solid fa-list-check",
        title: "Cleaner eligibility rolls",
        description: "Scheduled sweeps keep beneficiary and enrolment rolls accurate between formal reviews."
      },
      {
        icon: "fa-solid fa-file-lines",
        title: "Defensible audit trail",
        description: "Timestamped verification evidence supports improper-payment reporting and audit response."
      },
      {
        icon: "fa-solid fa-map-location-dot",
        title: "True nationwide reach",
        description: "Coverage across all fifty states, including recipients who relocated across state lines."
      }
    ],
    useCase: {
      title: "Where it fits in an agency workflow",
      body: "Agencies typically run a scheduled batch against the beneficiary roll immediately before each payment cycle, holding any matched record for caseworker confirmation. Real-time verification is added at the eligibility-determination step so a new application filed under a decedent identity is stopped at intake rather than after enrolment."
    },
    metrics: [
      { value: "7 Days", label: "Detection window", caption: "Typically identifies the death before the next monthly cycle." },
      { value: "50", label: "States covered", caption: "Including recipients who moved across jurisdictions." },
      { value: "Batch", label: "Pre-disbursement sweeps", caption: "Screen the full beneficiary roll ahead of every payment run." }
    ],
    faqs: [
      {
        question: "How does this compare to inter-agency death data sharing?",
        answer:
          "Inter-agency feeds are authoritative but slow, because a death has to be reported, processed and then propagated between systems. That chain commonly takes weeks. Sourcing directly from state and local jurisdictions compresses it to roughly a week, which is usually the difference between preventing a payment and attempting to recover one."
      },
      {
        question: "Can we run this against our full beneficiary roll on a schedule?",
        answer:
          "Yes. Batch verification is built for exactly this. Agencies commonly run the complete roll before each payment cycle and hold matched records for caseworker confirmation before any benefit is suspended."
      },
      {
        question: "What evidence do we get for audit and improper-payment reporting?",
        answer:
          "Every verification is logged with a reference ID, timestamp, the criteria used, the matched fields and the source classification. Prior results can be retrieved by reference ID at any time, which supports both audit response and individual appeal handling."
      },
      {
        question: "How is access controlled?",
        answer:
          "Access is contracted to vetted institutions with a documented permissible business purpose, and credentials are scoped per environment. Submission retention windows are configurable so the integration can be aligned with your agency's data-governance policy."
      }
    ]
  },
  {
    slug: "pension-and-retirement-funds",
    name: "Pension & Retirement Funds",
    navLabel: "Pension Funds",
    icon: "fa-solid fa-sack-dollar",
    metaTitle: "Death Verification for Pension Funds | Verify Deceased",
    metaDescription:
      "Detect deceased annuitants before the next payment run. Daily-refreshed death records for pension plans, retirement funds and administrators.",
    heroBadge: "Pension & Retirement",
    heroTitle: "Detect deceased annuitants",
    heroHighlight: "before the next payment run",
    heroDescription:
      "Pension overpayment after death is one of the most persistent leakage problems in retirement administration, and one of the most awkward to resolve, because recovery means approaching a grieving family.",
    painPoints: [
      "Monthly annuity payments continue for months after the annuitant has died",
      "Overpayment recovery is slow, costly and reputationally sensitive",
      "Annual proof-of-life mailings are expensive and easy to circumvent",
      "Fiduciary and actuarial reporting depends on an accurate mortality picture"
    ],
    outcomes: [
      {
        icon: "fa-solid fa-calendar-check",
        title: "Screen every payment cycle",
        description: "Run the full annuitant file before disbursement so payments stop at the right month."
      },
      {
        icon: "fa-solid fa-envelope-open-text",
        title: "Reduce proof-of-life costs",
        description: "Continuous data-driven screening cuts reliance on expensive annual attestation mailings."
      },
      {
        icon: "fa-solid fa-user-shield",
        title: "Meet fiduciary duty",
        description: "Demonstrate active, documented stewardship of plan assets to trustees and auditors."
      },
      {
        icon: "fa-solid fa-heart",
        title: "Kinder for families",
        description: "Preventing an overpayment avoids asking a bereaved family to return money months later."
      }
    ],
    useCase: {
      title: "Where it fits in plan administration",
      body: "The standard pattern is a batch screen of the full annuitant file a few days before each disbursement, with matches routed to a benefits analyst for confirmation and survivor-benefit processing. Larger plans layer continuous monitoring on top, so a match discovered mid-cycle triggers a webhook and the payment can be held immediately rather than waiting for the next scheduled run."
    },
    metrics: [
      { value: "Monthly", label: "Pre-payment screening", caption: "Catch the death in the same cycle it occurs." },
      { value: "7 Days", label: "Record availability", caption: "Fast enough to stop the very next payment." },
      { value: "Webhook", label: "Mid-cycle alerts", caption: "Hold a disbursement the moment a match is published." }
    ],
    faqs: [
      {
        question: "How much overpayment can pre-payment screening realistically prevent?",
        answer:
          "It depends on your population's mortality profile, but the mechanism is straightforward: each month of delay in detecting a death equals one additional full annuity payment issued in error. Compressing detection from roughly two months to roughly one week typically eliminates the majority of post-death payments entirely."
      },
      {
        question: "Can this replace our annual proof-of-life process?",
        answer:
          "It substantially reduces dependence on it. Continuous data-driven screening detects deaths far faster than an annual attestation cycle and cannot be circumvented by a family member returning the form. Many plans retain a reduced attestation process for specific populations while relying on screening for the bulk of the file."
      },
      {
        question: "Does it work for beneficiaries and survivors as well as primary annuitants?",
        answer:
          "Yes. Any identity in your file can be enrolled, including survivors receiving continuing benefits and beneficiaries on record for future payments."
      },
      {
        question: "How do we handle a match on someone who is actually alive?",
        answer:
          "Every result carries a confidence score and a matched-field breakdown, so you can set a high threshold for automatic action and route anything below it to analyst review. We recommend holding rather than terminating on first match, with confirmation before any benefit is permanently stopped."
      }
    ]
  },
  {
    slug: "employers-and-hr",
    name: "Employers & HR Screening",
    navLabel: "Employers & HR",
    icon: "fa-solid fa-briefcase",
    metaTitle: "Death Verification for Employers & HR | Verify Deceased",
    metaDescription:
      "Detect deceased identities used in job applications and onboarding. Fast, API-driven death verification for HR and background screening teams.",
    heroBadge: "Employers & HR",
    heroTitle: "Catch stolen identities",
    heroHighlight: "at onboarding, not at audit",
    heroDescription:
      "Employment fraud using a deceased person's Social Security number is one of the quietest forms of identity theft, and the employer usually discovers it during an audit, when remediation is at its most expensive.",
    painPoints: [
      "A decedent SSN passes standard onboarding and payroll validation",
      "Discovery typically happens at audit, long after wages have been reported",
      "Remediation involves payroll correction, tax filings and potential penalties",
      "Background screening providers relying on federal death files miss recent deaths entirely"
    ],
    outcomes: [
      {
        icon: "fa-solid fa-user-lock",
        title: "Screen at the offer stage",
        description: "Verify before onboarding so a decedent identity never enters payroll in the first place."
      },
      {
        icon: "fa-solid fa-file-invoice",
        title: "Avoid payroll remediation",
        description: "Prevention removes the corrected filings, penalties and rework that follow a late discovery."
      },
      {
        icon: "fa-solid fa-network-wired",
        title: "Strengthen screening products",
        description: "Background screening providers can add a genuinely current deceased check to their reports."
      },
      {
        icon: "fa-solid fa-gavel",
        title: "Support compliance posture",
        description: "Documented verification demonstrates reasonable diligence in your onboarding controls."
      }
    ],
    useCase: {
      title: "Where it fits in the hiring workflow",
      body: "The check runs alongside your existing background screening step, after a conditional offer and before payroll setup. Screening providers embed the same endpoint directly into their report generation so every consumer report includes a deceased-identity indicator sourced from records that are days rather than months old."
    },
    metrics: [
      { value: "Pre-hire", label: "Screening point", caption: "Verify before the identity reaches payroll systems." },
      { value: "7 Days", label: "Record freshness", caption: "Detects recent deaths that federal files have not published yet." },
      { value: "API", label: "Embeds in your stack", caption: "Drops straight into existing background screening pipelines." }
    ],
    faqs: [
      {
        question: "When in the hiring process should this run?",
        answer:
          "After a conditional offer and alongside your existing background screening, before payroll onboarding begins. Running it at that point means a decedent identity is caught before wages are reported, which avoids the payroll and tax remediation that follows a later discovery."
      },
      {
        question: "We already use a background screening provider. Why add this?",
        answer:
          "Most screening providers source deceased data from the Social Security Administration's Death Master File, which typically lags the actual death by one to three months. Recent deaths are precisely the identities being used fraudulently, so a DMF-based check is structurally blind to the highest-risk cases."
      },
      {
        question: "Can background screening companies resell this as part of their product?",
        answer:
          "Yes. Screening providers are a core API customer segment. The endpoint embeds directly into report generation so a current deceased-identity indicator appears on every consumer report you issue. Volume-based pricing tiers are available."
      },
      {
        question: "What do we need to submit for a reliable result?",
        answer:
          "Name and date of birth are the minimum. Including the SSN raises confidence significantly, and last known address helps disambiguate common names. The response returns exactly which fields matched so your team can judge each result on its merits."
      }
    ]
  },
  {
    slug: "healthcare-providers",
    name: "Healthcare & Pharmacy",
    navLabel: "Healthcare",
    icon: "fa-solid fa-stethoscope",
    metaTitle: "Death Verification for Healthcare & Pharmacy | Verify Deceased",
    metaDescription:
      "Prevent prescription and billing fraud using deceased patient identities. Nationwide death records refreshed daily for healthcare organizations.",
    heroBadge: "Healthcare & Pharmacy",
    heroTitle: "Protect patient records",
    heroHighlight: "from posthumous misuse",
    heroDescription:
      "A deceased patient's identity is a durable asset for prescription diversion and billing fraud, because no living patient will ever review the statement or dispute the claim.",
    painPoints: [
      "Controlled-substance prescriptions filled under a deceased patient's identity",
      "Claims and reimbursements billed for patients who have died",
      "Patient master indexes carry deceased records long after the fact",
      "Outreach and recall campaigns contact families of deceased patients"
    ],
    outcomes: [
      {
        icon: "fa-solid fa-prescription-bottle-medical",
        title: "Curb prescription diversion",
        description: "Flag decedent identities at the point of dispensing rather than during a retrospective audit."
      },
      {
        icon: "fa-solid fa-receipt",
        title: "Reduce billing fraud",
        description: "Screen claims against a current deceased index before reimbursement is issued."
      },
      {
        icon: "fa-solid fa-database",
        title: "Cleaner patient index",
        description: "Keep the master patient index accurate with scheduled deceased-status sweeps."
      },
      {
        icon: "fa-solid fa-comment-medical",
        title: "Respectful communications",
        description: "Suppress deceased patients from outreach so families are not contacted in error."
      }
    ],
    useCase: {
      title: "Where it fits in healthcare operations",
      body: "Payers integrate the check into pre-payment claim edits so a claim for a deceased member is held before reimbursement. Provider organizations and pharmacy networks run scheduled sweeps of the patient master index, both to flag potential diversion and to suppress deceased patients from recall, survey and marketing communications."
    },
    metrics: [
      { value: "Pre-pay", label: "Claim screening", caption: "Hold suspect claims before reimbursement is released." },
      { value: "Daily", label: "Index refresh", caption: "New decedent records published every day." },
      { value: "Bulk", label: "Patient index sweeps", caption: "Keep the master patient index current on a schedule." }
    ],
    faqs: [
      {
        question: "How is this used in prescription fraud prevention?",
        answer:
          "Pharmacy networks screen patient identities against the deceased index at or near the point of dispensing. Because our records typically appear within a week of death, a decedent identity being used for controlled-substance diversion is flagged while the pattern is still active rather than months later in a retrospective audit."
      },
      {
        question: "Can payers use this in pre-payment claim edits?",
        answer:
          "Yes. Real-time verification is fast enough to sit inside a pre-payment edit, so a claim submitted for a deceased member can be held for review before reimbursement is released rather than pursued afterwards through recovery."
      },
      {
        question: "Does this help keep our patient master index accurate?",
        answer:
          "Scheduled batch sweeps identify records that should be marked deceased. Beyond fraud prevention, this materially improves the quality of outreach and recall campaigns and avoids the distress caused by contacting the family of a deceased patient."
      },
      {
        question: "How is submitted patient data handled?",
        answer:
          "Submissions are encrypted in transit and at rest, retention windows are configurable to match your governance policy, and we never resell the identities you submit. Data-handling specifics are agreed as part of the onboarding and contracting process."
      }
    ]
  },
  {
    slug: "debt-collection-and-recovery",
    name: "Debt Collection & Recovery",
    navLabel: "Collections",
    icon: "fa-solid fa-file-invoice-dollar",
    metaTitle: "Death Verification for Debt Collection | Verify Deceased",
    metaDescription:
      "Suppress deceased debtors from collection campaigns. Daily-refreshed death records help agencies avoid compliance risk and wasted contact spend.",
    heroBadge: "Collections & Recovery",
    heroTitle: "Suppress deceased debtors",
    heroHighlight: "before the first contact attempt",
    heroDescription:
      "Contacting the family of a deceased debtor is a compliance incident, a complaint risk and a waste of contact spend. Accurate deceased suppression solves all three at once.",
    painPoints: [
      "Collection attempts directed at recently deceased consumers",
      "Complaints and regulatory exposure from contacting bereaved families",
      "Dialer, mail and letter spend wasted on unreachable accounts",
      "Estate and probate handling delayed because the death was never detected"
    ],
    outcomes: [
      {
        icon: "fa-solid fa-user-slash",
        title: "Accurate suppression",
        description: "Remove decedent accounts from dialer and mail campaigns before the first attempt goes out."
      },
      {
        icon: "fa-solid fa-shield-halved",
        title: "Lower compliance risk",
        description: "Fewer complaints arising from contact attempts directed at grieving families."
      },
      {
        icon: "fa-solid fa-coins",
        title: "Better contact economics",
        description: "Redirect dialer minutes and postage away from accounts that can never be worked."
      },
      {
        icon: "fa-solid fa-route",
        title: "Faster estate routing",
        description: "Detect the death early and move the account into the correct estate workflow immediately."
      }
    ],
    useCase: {
      title: "Where it fits in a collections operation",
      body: "Agencies screen each new placement file on receipt and re-screen the active inventory on a recurring schedule. Matched accounts are pulled out of dialing and mailing campaigns automatically and routed into the estate or probate workflow, which both reduces complaint volume and gets legitimate estate recovery moving sooner."
    },
    metrics: [
      { value: "On intake", label: "Placement screening", caption: "Screen every new placement file before work begins." },
      { value: "7 Days", label: "Record availability", caption: "Catches deaths that occurred after placement." },
      { value: "Bulk", label: "Inventory re-screening", caption: "Recurring sweeps across the full active inventory." }
    ],
    faqs: [
      {
        question: "When should we screen: at placement or continuously?",
        answer:
          "Both. Screening each placement file on receipt removes accounts where the consumer had already died, and recurring sweeps of active inventory catch deaths that occur after placement. The second is where most complaint risk actually originates."
      },
      {
        question: "Does this replace the deceased flags we get from the credit bureaus?",
        answer:
          "It complements them and closes their timing gap. Bureau deceased indicators depend on downstream reporting and commonly lag the death by months. Our records typically appear within a week, so the highest-risk period immediately after death is covered."
      },
      {
        question: "How does this reduce contact spend?",
        answer:
          "Every dialer minute, letter and text directed at a deceased consumer is unrecoverable spend with negative compliance value. Suppressing those accounts up front redirects the same budget toward accounts that can actually be resolved."
      },
      {
        question: "Can matched accounts route straight into an estate workflow?",
        answer:
          "Yes. The response includes the date of death where available, so your platform can automatically move the account into estate or probate handling and start the correct process rather than leaving it in a standard collection queue."
      }
    ]
  }
];

export const DvFaqs: FaqItem[] = [
  {
    question: "What is death verification and why does timing matter so much?",
    answer:
      "Death verification is the process of confirming whether a given identity belongs to someone who has died. Timing is the entire problem. Fraud committed with a deceased person's identity is concentrated in the first weeks after death, because that is when the identity still looks completely legitimate to every automated check. Our index typically reflects a death within about seven days, while the federal files most organizations rely on commonly take one to three months. That gap is where the losses occur."
  },
  {
    question: "How is Verify Deceased faster than the Social Security Death Master File?",
    answer:
      "The Death Master File is a downstream product. A death must be registered, processed and then propagated into the file before subscribers see it, and the public version also excludes records that states protect. We collect decedent records directly from source jurisdictions across all fifty states and publish daily, which removes several links from that chain and typically compresses availability from months to about a week."
  },
  {
    question: "Who is this product built for?",
    answer:
      "Institutions that carry real financial or compliance exposure when a deceased identity goes undetected: banks and credit unions, lenders and card issuers, insurance carriers, pension and retirement plans, government benefit agencies, healthcare payers and providers, employers and background screening firms, and debt collection agencies. Access is contracted to organizations with a documented permissible business purpose."
  },
  {
    question: "How do we access the data: API, batch, or both?",
    answer:
      "Both, and most customers use both. A real-time REST endpoint handles inline checks during onboarding, underwriting or claim intake with a median response under 300 milliseconds. Batch verification accepts up to 10,000 identities per request for portfolio sweeps. There is also a dashboard for manual lookups and file uploads, plus webhook-based continuous monitoring for enrolled portfolios."
  },
  {
    question: "What information do we need to submit for an accurate match?",
    answer:
      "Name and date of birth are the minimum. Adding a Social Security number raises confidence substantially, and a last known address helps disambiguate common names. Every response returns a confidence score plus the specific fields that matched, so you can set different action thresholds for different risk levels rather than treating all matches identically."
  },
  {
    question: "How accurate is the matching and how do we handle false positives?",
    answer:
      "Matching is deterministic and every result carries a transparent confidence score with a matched-field breakdown. The recommended pattern is to auto-action only high-confidence matches and route everything below your threshold to human review. For irreversible actions such as terminating a benefit, we recommend holding rather than terminating on first match and confirming before the change is made permanent."
  },
  {
    question: "How long does integration take?",
    answer:
      "Most engineering teams complete the integration in about a day. It is a single authenticated POST with a JSON body and a JSON response. We issue sandbox credentials with deterministic test identities up front, so your team can build and certify the full flow, including deceased, not-found and low-confidence paths, before contracting is finalized."
  },
  {
    question: "How is the data we submit protected?",
    answer:
      "Submissions are encrypted in transit using TLS 1.2 or higher and encrypted at rest. We do not resell the identities you submit to us. Retention windows for submitted data are configurable so the integration can be aligned with your own data-governance policy, and specifics are agreed during contracting."
  },
  {
    question: "What does it cost?",
    answer:
      "Pricing is volume-based and structured around your expected verification throughput, with separate tiers for real-time API calls, batch screening and continuous monitoring. Enterprise agreements include named integration support. Contact our team with your use case and expected volume for a quote."
  },
  {
    question: "What is the relationship between Verify Deceased and LeadCruncher?",
    answer:
      "Verify Deceased is a LeadCruncher product. LeadCruncher operates nationwide data infrastructure covering more than 160 million US properties with daily updates, and Verify Deceased applies that same source-level collection and daily-refresh discipline to decedent records for identity verification."
  }
];

export const DvApiFaqs: FaqItem[] = [
  {
    question: "How do I authenticate with the death verification API?",
    answer:
      "Every request carries a bearer token in the Authorization header. Keys are scoped per environment, so your sandbox key cannot reach production data and vice versa. Keys can be rotated from the dashboard at any time without downtime, and usage is tracked separately per key."
  },
  {
    question: "What is the response time and what are the rate limits?",
    answer:
      "Median response time for a single verification is under 300 milliseconds, which is fast enough to run inline in an onboarding or underwriting decision. Rate limits and throughput tiers are sized to your expected volume and agreed during onboarding rather than fixed at a single ceiling."
  },
  {
    question: "How does batch verification work?",
    answer:
      "Post up to 10,000 identities to the batch endpoint and receive a job reference immediately. The job is processed asynchronously and you can either poll for completion or register a webhook. Results come back with the same verdict, confidence score and matched-field structure as single verifications."
  },
  {
    question: "Is the API idempotent and safe to retry?",
    answer:
      "Yes. Every request accepts an idempotency key. If a network failure causes your client to retry, the original result is returned rather than a duplicate verification being created, so retries never double-bill or corrupt your audit trail."
  },
  {
    question: "How does continuous monitoring differ from one-off verification?",
    answer:
      "With monitoring you enrol a portfolio of identities once. Whenever a newly published decedent record matches an enrolled identity, we push a webhook to your endpoint. This is the right pattern for existing customer books, annuitant files and active collection inventory, where the risk is a death occurring after your initial check."
  },
  {
    question: "Can we test before signing a contract?",
    answer:
      "Yes. We issue sandbox credentials with deterministic test identities covering every response path, including deceased, not-found and deliberately low-confidence matches, so you can build and certify the complete integration before commercial terms are finalised."
  }
];

export const DvFraudFaqs: FaqItem[] = [
  {
    question: "What is deceased identity fraud?",
    answer:
      "Deceased identity fraud, often called ghosting, is the use of a dead person's identity to obtain credit, employment, benefits, insurance payouts or medical services. It is attractive to fraudsters because the victim cannot detect it, cannot report it and cannot dispute it, so the fraud frequently runs unchallenged until an institution absorbs the loss."
  },
  {
    question: "Why is the period right after death the highest risk?",
    answer:
      "Because the identity still appears completely valid. Credit files remain active, the Social Security number is not yet flagged in most commercial datasets, and the family is focused on grieving rather than monitoring accounts. Federal death files typically take one to three months to publish the death, so every automated check an institution runs during that window returns a clean result."
  },
  {
    question: "How is this different from synthetic identity fraud?",
    answer:
      "They overlap heavily. Synthetic identities are frequently constructed around a real Social Security number belonging to a deceased person, combined with a fabricated name and date of birth. Because the SSN is genuine, the profile survives basic validation. Checking the SSN against a current deceased index exposes the contradiction that conventional identity verification misses."
  },
  {
    question: "What are the warning signs institutions should watch for?",
    answer:
      "Common patterns include a credit application from an identity with a long, clean history but no recent activity; a sudden change of address or contact details shortly before an application; new accounts opened in quick succession across institutions; and applications where the SSN issue date is inconsistent with the stated date of birth. Any of these paired with a deceased-index match is a strong signal."
  },
  {
    question: "How can organizations actually prevent it rather than detect it late?",
    answer:
      "Prevention requires deceased data that is current enough to matter. Screening against a file that lags by one to three months is detection after the fact, not prevention. Screening at the point of decision against an index refreshed daily, and re-screening existing portfolios on a schedule, moves the control from recovery to prevention."
  },
  {
    question: "Which sectors are targeted most heavily?",
    answer:
      "Credit issuers and lenders see the highest volume because credit is the fastest way to convert a stolen identity into cash. Government benefit programmes, pension and retirement plans, insurance carriers and healthcare payers all carry substantial exposure, and employers are increasingly targeted because a deceased SSN can pass standard onboarding checks."
  }
];

export const DvDmfFaqs: FaqItem[] = [
  {
    question: "What is the Social Security Death Master File?",
    answer:
      "The Death Master File, or DMF, is a file maintained by the Social Security Administration containing records of reported deaths. A limited-access version is available to certified subscribers with a legitimate fraud-prevention purpose, while a public version excludes records that individual states protect. It has been the default deceased data source for decades, largely because there was no faster alternative."
  },
  {
    question: "Why does the Death Master File lag behind the actual date of death?",
    answer:
      "Because it sits at the end of a reporting chain. A death is registered locally, passed through state processing, reported to the Social Security Administration, then processed into the file and distributed to subscribers. Each hop adds delay, and the cumulative effect commonly puts availability one to three months after the death itself."
  },
  {
    question: "Is Verify Deceased a replacement for the DMF or a supplement to it?",
    answer:
      "Most customers treat it as the primary real-time control and retain the DMF where a specific regulation or contract names it. The practical value is coverage of the recent window the DMF cannot reach. If your obligation is to prevent fraud rather than to consume a specific named file, a faster source is a direct upgrade."
  },
  {
    question: "Do we still need Limited Access DMF certification?",
    answer:
      "Not for our service. Using Verify Deceased involves a standard commercial data agreement and an onboarding review confirming your permissible business purpose. It does not require you to hold or maintain Limited Access DMF certification, which removes the associated attestation and periodic audit overhead for organizations that only held it for fraud screening."
  },
  {
    question: "What is the engineering difference between the two?",
    answer:
      "The DMF is delivered as flat files, so you build and maintain the ingestion pipeline, storage, deduplication and matching logic yourself, and you own the accuracy of that matching. We deliver a single REST endpoint that returns a scored verdict directly, so most teams integrate in about a day and never operate matching infrastructure."
  },
  {
    question: "Does your data include state-protected records the public DMF omits?",
    answer:
      "We collect from source jurisdictions across all fifty states rather than consuming a downstream federal extract, so our coverage is not shaped by the exclusions applied to the public DMF. Coverage specifics for your particular use case are confirmed during onboarding."
  }
];
